import { Container } from "@chakra-ui/react"
import Hero from "@components/blocks/Hero"
import HeadingText from "@components/headings/HeadingText"
import NormalHeading from "@components/headings/NormalHeading"
import StylizedHeading from "@components/headings/StylizedHeading"
import HeaderLayout from "@components/layout/HeaderLayout"
import { EmenuPagesTranslation } from "@definitions/types"
import { readItem } from "@directus/sdk"
import { routing } from "@i18n/routing"
import ImageOne from "@public/images/hero/about-1.png"
import ImageTwo from "@public/images/hero/about-2.png"
import ImageThree from "@public/images/hero/about-3.png"
import ImageFour from "@public/images/hero/about-4.png"
import ImageFive from "@public/images/hero/about-5.png"
import SvgThumbsUp from "@public/svg/about/hero1.svg"
import SvgChart from "@public/svg/about/hero2.svg"
import SvgCart from "@public/svg/about/hero3.svg"
import SvgKptacLogo from "@public/svg/kptac-logo.svg"
import directusClient from "@utils/directus"
import { loadMessages } from "@utils/utils"
import { GetStaticProps, InferGetStaticPropsType, NextPage } from "next"
import dynamic from "next/dynamic"
import { useTranslations } from "next-intl"
import { NextSeo } from "next-seo"

const DynamicServices = dynamic(
  () => import("@components/blocks/about/Services"),
  { ssr: false }
)
const DynamicAboutStats = dynamic(
  () => import("@components/blocks/about/AboutStats"),
  { ssr: false }
)
const DynamicFooter = dynamic(() => import("@components/layout/Footer"), {
  ssr: false,
})

const About: NextPage = ({
  page_data,
  locales,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const t = useTranslations("about")

  return (
    <>
      <NextSeo
        title={page_data.meta_title}
        description={page_data.meta_description}
        canonical={`${process.env.NEXT_PUBLIC_SITE_URL}/${page_data.slug}`}
        languageAlternates={locales.map((lang: string) => {
          const prefix = lang === routing.defaultLocale ? "" : `/${lang}`
          return {
            hrefLang: lang,
            href: `${process.env.NEXT_PUBLIC_SITE_URL}${prefix}/${page_data.slug}`,
          }
        })}
        additionalMetaTags={[
          {
            name: "keywords",
            content: page_data.meta_keywords,
          },
        ]}
      />
      <HeaderLayout>
        <Hero
          iconOne={SvgChart}
          iconTwo={SvgKptacLogo}
          iconThree={SvgThumbsUp}
          iconFour={SvgCart}
          imageOne={ImageOne}
          imageTwo={ImageTwo}
          imageThree={ImageThree}
          imageFour={ImageFour}
          imageFive={ImageFive}
          alt={t("kptac")}
        />
      </HeaderLayout>
      <Container
        maxW={"container.xl"}
        px={{ base: 10, xl: 0 }}
      >
        <NormalHeading as={"h1"}>{t("heading")}</NormalHeading>
        <StylizedHeading>{t("sub_heading")}</StylizedHeading>
        <HeadingText>{t("heading_text")}</HeadingText>
        <NormalHeading>{t("summary")}</NormalHeading>
        <StylizedHeading>{t("services")}</StylizedHeading>
        <DynamicServices />
        <DynamicAboutStats />
      </Container>
      <DynamicFooter />
    </>
  )
}

export default About

export const getStaticProps: GetStaticProps = async ({ locale, locales }) => {
  // Page meta data
  const page_data = await directusClient
    .request(
      readItem("emenu_pages", 2, {
        fields: ["slug", { translations: ["*"] }],
      })
    )
    .then((res) => {
      const resultTranslations = res?.translations as EmenuPagesTranslation[]
      const translations = resultTranslations?.find(
        (t) => t.languages_id === locale
      )
      return {
        slug: res?.slug,
        meta_title: translations?.meta_title,
        meta_description: translations?.meta_description,
        meta_keywords: translations?.meta_keywords,
        title: translations?.title,
        content: translations?.content,
      }
    })

  const [shared, about] = await Promise.all([
    loadMessages("shared", locale ?? routing.defaultLocale),
    loadMessages("about", locale ?? routing.defaultLocale),
  ])

  return {
    props: {
      page_data,
      locales,
      messages: { ...shared, ...about },
    },
  }
}
